/**
 * Keeps the first N children of a grid visible and reveals the rest behind a
 * "View More" / "View Less" toggle.
 *
 * Deliberately counts children rather than measuring height: the editorial grid
 * is a CSS grid whose rows vary in height, and hiding whole tiles keeps the
 * grid's own `repeat(6, 1fr)` columns doing the layout work. Dependency-free
 * because it ships in a critical bundle that may run before jQuery.
 *
 * @module viewMore
 */

export const SELECTORS = {
  root: '.js-view-more',
  items: '.js-view-more-items',
  toggle: '.js-view-more-toggle',
};

export const EXPANDED_CLASS = 'is-expanded';

export const DEFAULT_VISIBLE = 6;

const ATTRIBUTES = {
  visible: 'data-view-more-visible',
  expandLabel: 'data-view-more-label',
  collapseLabel: 'data-view-less-label',
};

const DEFAULT_LABELS = {
  expand: 'View More',
  collapse: 'View Less',
};

/**
 * Reads how many children stay visible while collapsed, falling back to the
 * default when the attribute is missing or not a usable number.
 * @param {HTMLElement} root
 * @return {number}
 */
function visibleCount(root) {
  const parsed = parseInt(root.getAttribute(ATTRIBUTES.visible), 10);
  return parsed > 0 ? parsed : DEFAULT_VISIBLE;
}

/**
 * Wires up a single view-more block.
 * @param {HTMLElement} root An element matching `SELECTORS.root`.
 * @return {Object|null} Controller API, or null when the block is incomplete.
 */
export function initViewMoreBlock(root) {
  const items = root.querySelector(SELECTORS.items);
  const toggle = root.querySelector(SELECTORS.toggle);
  if (!items || !toggle) return null;

  const visible = visibleCount(root);
  const labels = {
    expand: toggle.getAttribute(ATTRIBUTES.expandLabel) || DEFAULT_LABELS.expand,
    collapse: toggle.getAttribute(ATTRIBUTES.collapseLabel) || DEFAULT_LABELS.collapse,
  };

  const children = Array.prototype.slice.call(items.children);
  // Nothing to reveal: leave every tile in place and drop the toggle. Also the
  // state a page whose JS never runs is left in.
  const overflows = children.length > visible;

  let expanded = false;

  function render() {
    children.forEach((child, index) => {
      child.hidden = !expanded && overflows && index >= visible;
    });

    root.classList.toggle(EXPANDED_CLASS, expanded);
    toggle.hidden = !overflows;
    toggle.textContent = expanded ? labels.collapse : labels.expand;
    toggle.setAttribute('aria-expanded', String(expanded));
  }

  function setExpanded(nextExpanded) {
    expanded = Boolean(nextExpanded) && overflows;
    render();
  }

  function onToggleClick(event) {
    event.preventDefault();
    setExpanded(!expanded);

    // Collapsing can leave the toggle above the fold with the page scrolled
    // past it, so pull it back into view.
    if (!expanded) {
      root.scrollIntoView({ block: 'nearest' });
      toggle.focus({ preventScroll: true });
    }
  }

  toggle.addEventListener('click', onToggleClick);
  render();

  return {
    expand: () => setExpanded(true),
    collapse: () => setExpanded(false),
    isExpanded: () => expanded,
    overflows: () => overflows,
    destroy() {
      toggle.removeEventListener('click', onToggleClick);
      root.classList.remove(EXPANDED_CLASS);
      children.forEach(child => { child.hidden = false; });
      toggle.hidden = false;
    },
  };
}

/**
 * Wires up every view-more block under `scope`.
 * @param {ParentNode} [scope=document] Where to look for blocks.
 * @return {Array<Object>} One controller per block that was wired up.
 */
export default function initViewMore(scope = document) {
  return Array.prototype.slice.call(scope.querySelectorAll(SELECTORS.root))
    .map(initViewMoreBlock)
    .filter(Boolean);
}



// WEBPACK FOOTER //
// ./src/scripts/modules/view-more.js